import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phoneNumber?: string;
  isEnabled?: boolean;
  loginDisabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListUsersData {
  listUsers: {
    items: User[];
    nextToken: string | null;
    totalCount: number;
  };
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}
import { Navigation } from '../../components/Navigation';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { UserManagementActions } from '../../components/admin/UserManagementActions';
import { ReactivationRequestList } from '../../components/user/ReactivationRequestList';
import { isAdmin, getRoleLabel, UserRole, STAFF_ROLES } from '../../utils/permissions';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_USERS = gql`
  query ListUsers($searchQuery: String, $role: String, $limit: Int, $nextToken: String) {
    listUsers(searchQuery: $searchQuery, role: $role, limit: $limit, nextToken: $nextToken) {
      items {
        id
        email
        firstName
        lastName
        role
        phoneNumber
        isEnabled
        loginDisabled
        createdAt
        updatedAt
      }
      nextToken
      totalCount
    }
  }
`;

export function UserManagement() {
  const navigate = useNavigate();
  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]); // Track tokens for each page
  const usersPerPage = 25;

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
      setPageTokens([null]);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination when role filter changes
  useEffect(() => {
    setCurrentPage(1);
    setPageTokens([null]);
  }, [roleFilter]);

  const { data, loading, error, refetch } = useQuery<ListUsersData>(LIST_USERS, {
    variables: {
      searchQuery: debouncedSearch || undefined,
      role: roleFilter !== 'ALL' ? roleFilter : undefined,
      limit: usersPerPage,
      nextToken: pageTokens[currentPage - 1] || undefined,
    },
    fetchPolicy: 'network-only',
  });

  // Handle query completion
  useEffect(() => {
    if (data?.listUsers) {
      console.log('✅ LIST_USERS Query completed:', data);
      console.log('Users count:', data?.listUsers?.items?.length);
      console.log('Total count:', data?.listUsers?.totalCount);
      
      // Store the nextToken for the next page
      if (data?.listUsers?.nextToken && currentPage === pageTokens.length) {
        setPageTokens(prev => [...prev, data.listUsers.nextToken]);
      }
    }
  }, [data, currentPage, pageTokens.length]);

  const user = userData?.getCurrentUser;
  const users = data?.listUsers?.items || [];
  const totalCount = data?.listUsers?.totalCount || 0;
  const hasNextPage = !!data?.listUsers?.nextToken;
  const totalPages = Math.ceil(totalCount / usersPerPage);

  // Calculate stats from total count (server provides accurate count)
  // For role-specific counts, we'll show them only when no filters are applied
  const showDetailedStats = !debouncedSearch && roleFilter === 'ALL';

  // Group current page users by role for display
  const usersByRole = {
    ADMIN: users.filter((u: any) => u.role === 'ADMIN'),
    ATTORNEY: users.filter((u: any) => u.role === 'ATTORNEY'),
    CASE_MANAGER: users.filter((u: any) => u.role === 'CASE_MANAGER'),
    CLIENT: users.filter((u: any) => u.role === 'CLIENT'),
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
  };

  const handleNextPage = () => {
    if (hasNextPage || currentPage < pageTokens.length) {
      setCurrentPage(p => p + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(p => p - 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-purple-100 text-purple-800';
      case 'ATTORNEY':
        return 'bg-blue-100 text-blue-800';
      case 'CASE_MANAGER':
        return 'bg-green-100 text-green-800';
      case 'OFFICE_MANAGER':
        return 'bg-teal-100 text-teal-800';
      case 'INQUIRY_MANAGER':
        return 'bg-cyan-100 text-cyan-800';
      case 'LEAD_CASE_MANAGER':
        return 'bg-emerald-100 text-emerald-800';
      case 'MEDICAL_RECORDS_COORDINATOR':
        return 'bg-indigo-100 text-indigo-800';
      case 'PARALEGAL':
        return 'bg-violet-100 text-violet-800';
      case 'CLIENT':
        return 'bg-gray-100 text-gray-800';
      case 'PROSPECT':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Use the imported getRoleLabel from permissions.ts instead of local function

  if (loading) {
    console.log('⏳ Loading users...');
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  if (error) {
    console.error('💥 Error state:', error);
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <h2 className="text-xl font-bold text-red-600">Error Loading Users</h2>
              <p className="text-gray-600 mt-2">{error.message}</p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Only admins can access user management
  if (!user || !isAdmin(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <h2 className="text-xl font-bold text-red-600">Access Denied</h2>
              <p className="text-gray-600 mt-2">Only administrators can access user management.</p>
              <Button onClick={() => navigate('/dashboard')} className="mt-4">
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb />

        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600 mt-1">Manage all system users and their permissions</p>
            </div>
            <Button onClick={() => navigate('/admin/users/new')}>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create User
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => handleRoleFilterChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
              >
                <option value="ALL">All Roles</option>
                <option value="ADMIN">{getRoleLabel(UserRole.ADMIN)}</option>
                <option value="ATTORNEY">{getRoleLabel(UserRole.ATTORNEY)}</option>
                <option value="CASE_MANAGER">{getRoleLabel(UserRole.CASE_MANAGER)}</option>
                <option value="OFFICE_MANAGER">{getRoleLabel(UserRole.OFFICE_MANAGER)}</option>
                <option value="INQUIRY_MANAGER">{getRoleLabel(UserRole.INQUIRY_MANAGER)}</option>
                <option value="LEAD_CASE_MANAGER">{getRoleLabel(UserRole.LEAD_CASE_MANAGER)}</option>
                <option value="MEDICAL_RECORDS_COORDINATOR">{getRoleLabel(UserRole.MEDICAL_RECORDS_COORDINATOR)}</option>
                <option value="PARALEGAL">{getRoleLabel(UserRole.PARALEGAL)}</option>
                <option value="CLIENT">{getRoleLabel(UserRole.CLIENT)}</option>
                <option value="PROSPECT">{getRoleLabel(UserRole.PROSPECT)}</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Total Users</div>
              <div className="text-2xl font-bold text-gray-900">
                {totalCount.toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">
                {showDetailedStats ? 'Staff Members' : 'Staff (this page)'}
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {usersByRole.ADMIN.length + usersByRole.ATTORNEY.length + usersByRole.CASE_MANAGER.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">
                {showDetailedStats ? 'Clients' : 'Clients (this page)'}
              </div>
              <div className="text-2xl font-bold text-gray-900">{usersByRole.CLIENT.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Active (this page)</div>
              <div className="text-2xl font-bold text-gray-900">
                {users.filter((u: any) => u.isEnabled !== false).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reactivation Requests Section */}
        <div className="mb-6">
          <ReactivationRequestList 
            statusFilter="PENDING" 
            limit={5}
            compact={true}
          />
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {debouncedSearch || roleFilter !== 'ALL' 
                  ? `Filtered Results (${totalCount.toLocaleString()} total)`
                  : `All Users (${totalCount.toLocaleString()})`
                }
              </CardTitle>
              {totalPages > 0 && (
                <div className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages > 0 ? totalPages : 1} • Showing {users.length} users
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        {loading ? 'Loading...' : 'No users found'}
                      </td>
                    </tr>
                  ) : (
                    users.map((u: any) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-aalaw-red rounded-full flex items-center justify-center text-white font-semibold">
                              {u.firstName?.[0]}{u.lastName?.[0]}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {u.firstName} {u.lastName}
                              </div>
                              {u.phoneNumber && (
                                <div className="text-sm text-gray-500">{u.phoneNumber}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{u.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(u.role)}`}>
                            {getRoleLabel(u.role)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {u.loginDisabled ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Login Blocked
                            </span>
                          ) : u.isEnabled === false ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Restricted
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(u.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <UserManagementActions
                            user={u}
                            currentUserRole={user.role}
                            onActionComplete={() => refetch()}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {(totalPages > 1 || hasNextPage) && (
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                <div className="flex flex-1 justify-between sm:hidden">
                  <Button
                    variant="outline"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleNextPage}
                    disabled={(!hasNextPage && currentPage >= pageTokens.length) || loading}
                  >
                    Next
                  </Button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span>
                      {totalPages > 0 && (
                        <> of <span className="font-medium">{totalPages}</span></>
                      )}
                      {' '}• <span className="font-medium">{totalCount.toLocaleString()}</span> total users
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleFirstPage}
                      disabled={currentPage === 1 || loading}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1 || loading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={(!hasNextPage && currentPage >= pageTokens.length) || loading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
